import React, { useEffect, useRef, useState } from 'react'
import { Col, Icon, Modal, notification, Row } from 'antd'
import { Formik } from 'formik'
import { useSelector } from 'react-redux'

import { ApplicationState } from 'src/store'
import { Button, Table, Text } from 'src/components/Ant'
import { ColumnProps } from 'src/components/Ant/Table'
import {
  Id,
  PolicyFlowClosing,
  PolicyFlowFormat,
  PolicyFlowType,
  PolicyTree
} from 'src/types'
import { Select } from 'src/components/Formik'
import { useT } from 'src/core/hooks/use-translation'
import * as PoliciesService from 'src/services/api/policies-flow'
import * as PoliciesTreeService from 'src/services/api/policies-trees'

import { PropsTreeSelected } from '../..'
import FlowClosingsModal from '../../../flow/closings/flow-closings-modal'

type HierarchyFlowsClosingsProps = {
  policyTree: PolicyTree[]
  resetPoliciesTrees: () => void
  treesSelected: PropsTreeSelected
}
type PolicyFlowClosingList = PolicyFlowClosing & { key: Id }

export default function HierarchyFlowsClosings({
  policyTree,
  resetPoliciesTrees,
  treesSelected
}: HierarchyFlowsClosingsProps) {
  const { me } = useSelector((state: ApplicationState) => ({
    me: state.me
  }))

  const refFormik = useRef()
  const refFormikClosingModal = useRef<Formik<PolicyFlowClosing>>(null)

  const [dataSource, setDataSource] = useState<PolicyFlowClosingList[]>([])
  const [loading, setLoading] = useState(false)

  const { t } = useT()

  const [modalPolicyFlowFormat, setModalPolicyFlowFormat] =
    useState<PolicyFlowFormat>(PolicyFlowFormat.STANDARD)
  const [modalPolicyFlowType, setModalPolicyFlowType] =
    useState<PolicyFlowType>(PolicyFlowType.HIERARCHY)

  function setModalPolicyFlow(record: PolicyFlowClosing): void {
    let type = PolicyFlowType.HIERARCHY
    let format = PolicyFlowFormat.STANDARD

    if (record.cost_center_closing) {
      type = PolicyFlowType.COST_CENTER
    }

    if (record.automatic_closing) {
      format = PolicyFlowFormat.AUTOMATIC
    }

    if (record.consensual_closing) {
      format = PolicyFlowFormat.CONSENSUS
    }

    setModalPolicyFlowFormat(format)
    setModalPolicyFlowType(type)
  }

  useEffect(() => {
    setLoading(true)
    PoliciesService.getPolicyClosings()
      .then(response => {
        if (response.status === 200) {
          const closings: PolicyFlowClosingList[] =
            response.data?.policy_closings.map(data => ({
              ...data,
              key: data.policy_closing_id
            })) || []
          setDataSource(closings)
        } else {
          notification.error({
            message: t('common.response.status-500'),
            placement: 'bottomRight'
          })
        }
      })
      .catch(() => {
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
        //Send Bugsnag?
      })
      .finally(() => {
        setLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function LinkPolicyTree(value: any) {
    Modal.confirm({
      cancelText: t('common.actions.cancel'),
      content: t('web.tree.policies.modal.message'),
      onOk: () =>
        PoliciesTreeService.save({
          assign_tree_id: treesSelected.id,
          organization_id: me.organization.organization_id,
          policy: 'policy_closing_id',
          policy_id: value.policy
        })
          .then(response => {
            if (response.status === 200) {
              resetPoliciesTrees()
            } else {
              notification.error({
                message: t('common.response.status-500'),
                placement: 'bottomRight'
              })
            }
          })
          .catch(() => {
            notification.error({
              message: t('common.response.status-500'),
              placement: 'bottomRight'
            })
          })
    })
  }

  const columns: ColumnProps<PolicyTree>[] = [
    {
      dataIndex: 'policy_closing.code',
      key: 'code',
      title: 'web.tree.flows.table.code'
    },
    {
      dataIndex: 'policy_closing.name',
      key: 'name',
      title: 'web.tree.flows.table.name'
    },
    {
      align: 'center',
      dataIndex: 'policy_closing',
      key: 'view',
      render: (row: PolicyFlowClosing) => (
        <Icon
          type="eye"
          onClick={() => {
            setModalPolicyFlow(row)
            refFormikClosingModal.current.resetForm(row)
          }}
        />
      ),
      title: 'web.tree.flows.table.view'
    }
  ]

  const optionsClosing = dataSource.map(closing => ({
    value: String(closing.policy_closing_id),
    label: closing.name
  }))

  return (
    <>
      <Formik
        initialValues={{ policy: '' }}
        onSubmit={LinkPolicyTree}
        ref={refFormik}
        render={formikProps => (
          <Row>
            <Col span={15}>
              <Select
                dataTestId="organizations-list"
                formikProps={formikProps}
                label={'web.tree.flows.closings.flow-title'}
                name="policy"
                options={optionsClosing}
                placeholder="common.actions.select"
                required={false}
              />
            </Col>
            <Col span={9}>
              <Button
                disabled={formikProps.values.policy === ''}
                type="default"
                onClick={() => {
                  const foundPolicy = dataSource.find(
                    policy =>
                      `${policy.policy_closing_id}` ===
                      `${formikProps.values.policy}`
                  )
                  if (foundPolicy) {
                    refFormikClosingModal.current.resetForm(foundPolicy)
                    setModalPolicyFlow(foundPolicy)
                  }
                }}
                style={{ margin: '40px 10px 0px 10px' }}
              >
                <Icon key="eye" type="eye" />
              </Button>

              <Button
                disabled={formikProps.values.policy === ''}
                onClick={() => {
                  formikProps.submitForm()
                }}
              >
                {t('web.tree.policies.link')}
              </Button>
            </Col>
          </Row>
        )}
      />

      <div>
        <Text style={{ fontSize: '25px', color: '#000' }} strong>
          {t('web.tree.flows.flow-link')}
        </Text>
        <Table
          dataSource={policyTree?.[0]?.policy_closing_id ? policyTree : []}
          loading={loading}
          className="table-striped"
          columns={columns}
          size="small"
        />
      </div>
      <Formik
        initialValues={{} as any}
        onSubmit={undefined}
        ref={refFormikClosingModal}
        render={formikProps => (
          <FlowClosingsModal
            disabled
            formikProps={formikProps}
            isVisible={Boolean(formikProps?.values?.policy_closing_id)}
            modalPolicyFlowFormat={modalPolicyFlowFormat}
            modalPolicyFlowType={modalPolicyFlowType}
            refFormik={refFormikClosingModal}
          />
        )}
      />
    </>
  )
}
